import {
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  Circle,
  Info,
  MessageSquare,
  Plus,
} from "lucide-react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ScaledThumbnail } from "./ScaledThumbnail";

interface ActionsCardThumbnailProps {
  isHovered?: boolean;
  alwaysVisible?: boolean;
}

const ACTIONS = [
  {
    title: "Chase Acme Corp overdue invoice",
    dueDate: "8 Aug 2026",
    overdue: true,
    comments: 2,
  },
  {
    title: "Confirm payroll funding for August",
    dueDate: "14 Aug 2026",
    overdue: false,
    comments: 0,
  },
  {
    title: "Review uncategorised transactions",
    dueDate: "18 Aug 2026",
    overdue: false,
    comments: 1,
  },
];

export default function ActionsCardThumbnail({
  isHovered = false,
  alwaysVisible = false,
}: ActionsCardThumbnailProps) {
  return (
    <ScaledThumbnail
      isHovered={isHovered}
      alwaysVisible={alwaysVisible}
      contentWidth={340}
    >
      <Card className="w-[340px] flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between border-b">
          <div className="flex items-center gap-1">
            <span className="text-lg font-semibold text-neutral-900">
              Actions
            </span>
            <Info className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
            <Plus className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-0 flex flex-col">
          <div className="px-6 pt-4">
            <div className="w-full h-8 inline-flex items-center gap-1 rounded-lg bg-muted p-1">
              <span className="flex-1 h-7 flex items-center justify-center gap-1.5 text-xs rounded-md bg-background font-medium text-neutral-900 shadow-sm">
                Assigned to Me
                <Badge
                  variant="secondary"
                  className="h-4 px-1 text-xs leading-none"
                >
                  3
                </Badge>
              </span>
              <span className="flex-1 h-7 flex items-center justify-center gap-1.5 text-xs rounded-md text-muted-foreground">
                Delegated
                <Badge
                  variant="secondary"
                  className="h-4 px-1 text-xs leading-none"
                >
                  2
                </Badge>
              </span>
            </div>

            <div className="mt-2 space-y-1.5 pb-4">
              {ACTIONS.map((action) => (
                <div
                  key={action.title}
                  className={`flex items-center gap-2 p-2.5 border rounded-md ${
                    action.overdue
                      ? "border-destructive/40 bg-destructive/5"
                      : ""
                  }`}
                >
                  <Circle className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">
                      {action.title}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                      <span className="flex items-center gap-0.5">
                        <CalendarDays className="h-3 w-3" />
                        {action.dueDate}
                      </span>
                    </div>
                  </div>
                  {action.overdue && (
                    <AlertTriangle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />
                  )}
                  <div className="relative flex-shrink-0 text-muted-foreground">
                    <MessageSquare className="h-3.5 w-3.5" />
                    {action.comments > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 h-3.5 w-3.5 rounded-full bg-primary text-primary-foreground text-[9px] flex items-center justify-center font-medium leading-none">
                        {action.comments}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="px-6 py-3 border-t flex justify-end">
            <Button variant="ghost" size="sm" className="text-neutral-900">
              View all actions
              <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </ScaledThumbnail>
  );
}

export { ActionsCardThumbnail };
